/**
 * Ambient falling-leaf particles (vanilla 1.20 cherry petals, 1.21.5 leaves).
 *
 * Vanilla's animateTick picks random blocks around the player every client
 * tick; a leaves block whose underside is open then sheds a particle with a
 * small chance (cherry 10 %, pale oak 2 %, other leaves 1 %). We sample fewer
 * blocks per tick and scale the chances up so the density under a canopy stays
 * the same. The motion is a port of FallingLeavesParticle: constant gentle
 * gravity, a wind push that grows with age, a slowly accelerating spin.
 *
 * Like BlockParticles this is a fixed CPU pool drawn as one THREE.Points; the
 * petal outline is cut in the fragment shader. Lighting mirrors the voxel
 * shader (render/materials.ts) using the shared env uniforms and the voxel
 * light at the particle, so petals dim at night and in the shade.
 */

import * as THREE from 'three';
import {
  ACACIA_LEAVES_ID, BIRCH_LEAVES_ID, BLOCKS, CHERRY_LEAVES_ID, DARK_OAK_LEAVES_ID, IS_SOLID, IS_WATERY,
  JUNGLE_LEAVES_ID, LEAVES_ID, MANGROVE_LEAVES_ID, PALE_OAK_LEAVES_ID, SPRUCE_LEAVES_ID, tintOf,
} from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import type { World } from '../world/chunk';
import type { EnvUniforms } from './env';
import { LIGHTMAP_GLSL } from './lightmap';

const POOL = 1536;
const TICK_RATE = 20;

/** Random block picks per 20 Hz tick, in a box around the camera. */
const SAMPLES_PER_TICK = 200;
const RANGE_XZ = 16;
const RANGE_Y = 12;
/**
 * Vanilla lands ~850 of its 2 x 667 picks per tick inside this box; we make
 * SAMPLES_PER_TICK, so each chance is scaled by that ratio.
 */
const DENSITY = 850 / SAMPLES_PER_TICK;

/** Vanilla particle lifetime: 300 ticks. */
const LIFETIME = 300 / TICK_RATE;
const FADE_IN = 0.25;
const FADE_OUT = 1.0;
/** A landed petal rests briefly while it fades. */
const SETTLE = 0.7;
const DESPAWN_DIST = 56;

const enum Kind { None = 0, Cherry = 1, Leaf = 2, PaleLeaf = 3 }

interface KindDef {
  /** Spawn chance per pick (vanilla chance x DENSITY). */
  chance: number;
  /** blocks / s^2 (vanilla gravity is per tick^2: x400). */
  gravity: number;
  /** Initial downward speed, blocks / s. */
  fall: number;
  /** Cherry: accelerate away along a per-particle heading. */
  flowAway: boolean;
  /** Leaves: swirling wind, vanilla "windBig". */
  swirl: number;
  /** Sprite edge length = scale x (0.1 | 0.15) blocks. */
  scale: number;
}

const KINDS: Record<number, KindDef> = {
  [Kind.Cherry]: { chance: 0.1 * DENSITY, gravity: 0.00075 * 400, fall: 0, flowAway: true, swirl: 0, scale: 1 },
  [Kind.Leaf]: { chance: 0.01 * DENSITY, gravity: 0.00021 * 400, fall: 0.021 * TICK_RATE, flowAway: false, swirl: 10, scale: 2 },
  [Kind.PaleLeaf]: { chance: 0.02 * DENSITY, gravity: 0.00021 * 400, fall: 0.021 * TICK_RATE, flowAway: false, swirl: 10, scale: 2 },
};

const LEAF_KIND = new Uint8Array(BLOCKS.length);
LEAF_KIND[CHERRY_LEAVES_ID] = Kind.Cherry;
for (const id of [
  LEAVES_ID, BIRCH_LEAVES_ID, JUNGLE_LEAVES_ID, ACACIA_LEAVES_ID, DARK_OAK_LEAVES_ID, MANGROVE_LEAVES_ID,
  SPRUCE_LEAVES_ID,
]) {
  LEAF_KIND[id] = Kind.Leaf;
}
LEAF_KIND[PALE_OAK_LEAVES_ID] = Kind.PaleLeaf;

/** Vanilla cherry petal palette (sRGB). */
const PETAL_COLORS = [
  [0xf9, 0xc0, 0xdc], [0xf4, 0xa5, 0xcb], [0xfb, 0xd2, 0xe6], [0xe8, 0x8f, 0xbb],
].map((c) => c.map((v) => v / 255));
const PALE_LEAF = [0.62, 0.66, 0.6];

const VERT = /* glsl */ `
uniform vec3 uSunColor;
uniform vec3 uAmbientColor;
uniform float uGamma;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uViewHeight;
attribute vec4 aColor;  // sRGB colour, opacity
attribute vec4 aMisc;   // size (blocks), roll (rad), skylight 0..1, blocklight 0..1
varying vec4 vColor;
varying vec3 vShape;    // cos(roll), sin(roll), tumble foreshortening

// same curve + scales as the voxel shader (render/materials.ts)
${LIGHTMAP_GLSL}

void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  float px = aMisc.x * projectionMatrix[1][1] * uViewHeight * 0.5 / max(-mv.z, 0.05);
  float alpha = aColor.a * min(px / 1.5, 1.0); // sub-pixel petals fade instead of shimmering
  gl_PointSize = clamp(px, 1.5, 160.0);
  if (alpha <= 0.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0); // dead: clip it

  vec3 light = uAmbientColor * (0.62 * lightCurve(aMisc.z))
             + uSunColor * (0.30 * 0.6 * smoothstep(0.55, 0.95, aMisc.z))
             + vec3(1.75, 1.5, 1.2) * blockLightTint(lightCurve(aMisc.w))
             + vec3(0.010, 0.012, 0.018);
  vec3 color = pow(aColor.rgb, vec3(2.2)) * light;

  vec3 toCam = cameraPosition - position;
  float fog = smoothstep(uFogNear, uFogFar, max(length(toCam.xz), abs(toCam.y) * 0.7));
  vColor = vec4(mix(color, uFogColor, fog), alpha);

  // a petal tumbles as it falls: squash it across its width
  vShape = vec3(cos(aMisc.y), sin(aMisc.y), 0.4 + 0.6 * abs(cos(aMisc.y * 1.7 + 1.3)));
}`;

const FRAG = /* glsl */ `
varying vec4 vColor;
varying vec3 vShape;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  vec2 q = vec2(vShape.x * p.x + vShape.y * p.y, vShape.x * p.y - vShape.y * p.x);
  // teardrop: round at the base (-x), tapering to the tip (+x)
  float halfWidth = (0.5 - 0.22 * q.x) * vShape.z;
  float d = q.x * q.x / 0.846 + q.y * q.y / (halfWidth * halfWidth);
  float a = 1.0 - smoothstep(0.65, 1.0, d);
  if (a < 0.01) discard;
  float shade = 0.8 + 0.2 * smoothstep(-0.9, 0.5, q.x);
  gl_FragColor = vec4(vColor.rgb * shade, vColor.a * a);
}`;

export class AmbientParticles {
  readonly points: THREE.Points;
  /** Live particle count (debug). */
  alive = 0;

  private geom: THREE.BufferGeometry;
  private pos: Float32Array;
  private col: Float32Array;
  private misc: Float32Array;

  // per-particle simulation state
  private x = new Float32Array(POOL);
  private y = new Float32Array(POOL);
  private z = new Float32Array(POOL);
  private vx = new Float32Array(POOL);
  private vy = new Float32Array(POOL);
  private vz = new Float32Array(POOL);
  private age = new Float32Array(POOL);
  /** 0 = dead, otherwise the Kind. */
  private kind = new Uint8Array(POOL);
  /** > 0 once landed: seconds of rest left. */
  private settle = new Float32Array(POOL);
  private spin = new Float32Array(POOL);
  private spinAccel = new Float32Array(POOL);
  /** Per-particle random 0..1 (wind heading, flutter phase). */
  private seed = new Float32Array(POOL);
  private sky = new Float32Array(POOL);
  private blk = new Float32Array(POOL);

  private cursor = 0;
  private sampleAcc = 0;

  constructor(
    private readonly world: World,
    env: EnvUniforms
  ) {
    this.pos = new Float32Array(POOL * 3);
    this.col = new Float32Array(POOL * 4);
    this.misc = new Float32Array(POOL * 4);

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    this.geom.setAttribute('aColor', new THREE.BufferAttribute(this.col, 4));
    this.geom.setAttribute('aMisc', new THREE.BufferAttribute(this.misc, 4));
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    const uViewHeight = { value: 720 };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      // env uniforms are shared by reference: the Sky updates them every frame
      uniforms: {
        uSunColor: env.uSunColor,
        uAmbientColor: env.uAmbientColor,
        uGamma: env.uGamma,
        uFogColor: env.uFogColor,
        uFogNear: env.uFogNear,
        uFogFar: env.uFogFar,
        uViewHeight,
      },
      transparent: true,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geom, mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10; // after water, so petals over a lake aren't tinted by it
    // point sprites are sized in pixels: track the height of whatever we render into
    const size = new THREE.Vector2();
    this.points.onBeforeRender = (renderer) => {
      const target = renderer.getRenderTarget();
      uViewHeight.value = target ? target.height : renderer.getDrawingBufferSize(size).y;
    };
  }

  /** Advance the simulation and shed new particles around `cam` (world position). */
  update(dt: number, cam: THREE.Vector3): void {
    this.spawnAround(dt, cam);
    if (this.alive === 0) return;

    const { world, pos, col, misc } = this;
    let alive = 0;
    for (let i = 0; i < POOL; i++) {
      const kind = this.kind[i];
      if (kind === Kind.None) continue;
      const def = KINDS[kind];
      const age = (this.age[i] += dt);
      let opacity = Math.min(1, age / FADE_IN, (LIFETIME - age) / FADE_OUT);
      let dead = age >= LIFETIME;

      if (this.settle[i] > 0) {
        // resting where it landed
        this.settle[i] -= dt;
        opacity = Math.min(opacity, this.settle[i] / SETTLE);
        if (this.settle[i] <= 0) dead = true;
      } else {
        const g = Math.min(age / LIFETIME, 1);
        let ax = 0, az = 0;
        if (def.flowAway) {
          const heading = this.seed[i] * (Math.PI / 3);
          const push = 2 * Math.pow(g, 1.25);
          ax += Math.cos(heading) * push;
          az += Math.sin(heading) * push;
        }
        if (def.swirl > 0) {
          // vanilla swirlPeriod: 1000..4000 degrees over the lifetime
          const phase = g * (17.45 + this.seed[i] * 52.36);
          ax += g * Math.cos(phase) * def.swirl;
          az += g * Math.sin(phase) * def.swirl;
        }
        // vanilla: velocity += wind * 0.0025 per tick^2  ->  wind * 1.0 blocks / s^2
        this.vx[i] += ax * dt;
        this.vz[i] += az * dt;
        this.vy[i] -= def.gravity * dt;
        this.spin[i] += this.spinAccel[i] * dt;
        misc[i * 4 + 1] += this.spin[i] * dt;

        const oy = this.y[i];
        const nx = this.x[i] + this.vx[i] * dt;
        const ny = oy + this.vy[i] * dt;
        const nz = this.z[i] + this.vz[i] * dt;
        const bx = Math.floor(nx), by = Math.floor(ny), bz = Math.floor(nz);
        const id = world.getBlock(bx, by, bz);
        if (IS_SOLID[id] === 1 || IS_WATERY[id] === 1) {
          // landed (or blew into a wall): rest on the surface and fade
          this.settle[i] = SETTLE;
          if (by < Math.floor(oy)) {
            this.x[i] = nx;
            this.z[i] = nz;
            this.y[i] = Math.floor(oy) + (IS_WATERY[id] === 1 ? -0.09 : 0.03);
          }
          this.x[i] += this.flutterX(i, age);
          this.z[i] += this.flutterZ(i, age);
        } else {
          this.x[i] = nx;
          this.y[i] = ny;
          this.z[i] = nz;
          // follow the voxel light as it falls through shade
          const k = Math.min(1, dt * 6);
          this.sky[i] += (world.getSky(bx, by, bz) / 15 - this.sky[i]) * k;
          this.blk[i] += (world.getBlockLight(bx, by, bz) / 15 - this.blk[i]) * k;
        }
      }

      const dx = this.x[i] - cam.x, dz = this.z[i] - cam.z;
      if (dead || dx * dx + dz * dz > DESPAWN_DIST * DESPAWN_DIST) {
        this.kind[i] = Kind.None;
        col[i * 4 + 3] = 0;
        continue;
      }
      alive++;
      const resting = this.settle[i] > 0;
      pos[i * 3] = this.x[i] + (resting ? 0 : this.flutterX(i, age));
      pos[i * 3 + 1] = this.y[i];
      pos[i * 3 + 2] = this.z[i] + (resting ? 0 : this.flutterZ(i, age));
      col[i * 4 + 3] = Math.max(0, opacity);
      misc[i * 4 + 2] = this.sky[i];
      misc[i * 4 + 3] = this.blk[i];
    }
    this.alive = alive;

    (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute('aColor') as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute('aMisc') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Side-to-side flutter, layered on the simulated path. */
  private flutterX(i: number, age: number): number {
    return Math.sin(age * 2.3 + this.seed[i] * 40) * 0.07;
  }
  private flutterZ(i: number, age: number): number {
    return Math.cos(age * 1.7 + this.seed[i] * 71) * 0.07;
  }

  /** Vanilla animateTick: random block picks near the camera shed particles. */
  private spawnAround(dt: number, cam: THREE.Vector3): void {
    this.sampleAcc += dt * TICK_RATE * SAMPLES_PER_TICK;
    const n = Math.floor(this.sampleAcc);
    this.sampleAcc -= n;
    const world = this.world;
    const cx = Math.floor(cam.x), cy = Math.floor(cam.y), cz = Math.floor(cam.z);
    for (let s = 0; s < n; s++) {
      const by = cy + Math.floor((Math.random() * 2 - 1) * RANGE_Y);
      if (by < 1 || by >= WORLD_HEIGHT) continue;
      const bx = cx + Math.floor((Math.random() * 2 - 1) * RANGE_XZ);
      const bz = cz + Math.floor((Math.random() * 2 - 1) * RANGE_XZ);
      const id = world.getBlock(bx, by, bz);
      const kind = LEAF_KIND[id];
      if (kind === Kind.None) continue;
      if (Math.random() >= KINDS[kind].chance) continue;
      // only from an open underside (vanilla: block below has no full top face)
      if (IS_SOLID[world.getBlock(bx, by - 1, bz)] === 1) continue;
      this.spawn(kind, id, bx + Math.random(), by - 0.05, bz + Math.random(), bx, by - 1, bz);
    }
  }

  private spawn(kind: Kind, blockId: number, x: number, y: number, z: number, lx: number, ly: number, lz: number): void {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % POOL;
    if (this.kind[i] === Kind.None) this.alive++;
    const def = KINDS[kind];
    this.kind[i] = kind;
    this.x[i] = x;
    this.y[i] = y;
    this.z[i] = z;
    this.vx[i] = 0;
    this.vy[i] = -def.fall;
    this.vz[i] = 0;
    this.age[i] = 0;
    this.settle[i] = 0;
    this.seed[i] = Math.random();
    // vanilla: +-30 deg/s roll, accelerating by +-5 deg/s^2
    this.spin[i] = (Math.random() < 0.5 ? -1 : 1) * (Math.PI / 6);
    this.spinAccel[i] = (Math.random() < 0.5 ? -1 : 1) * (Math.PI / 36);
    this.sky[i] = this.world.getSky(lx, ly, lz) / 15;
    this.blk[i] = this.world.getBlockLight(lx, ly, lz) / 15;

    let r: number, g: number, b: number;
    const vary = 0.92 + Math.random() * 0.12;
    if (kind === Kind.Cherry) {
      const c = PETAL_COLORS[(Math.random() * PETAL_COLORS.length) | 0];
      r = c[0] * vary; g = c[1] * vary; b = c[2] * vary;
    } else {
      // leaves: the block's foliage tint over a mid-grey leaf texel
      const t = kind === Kind.PaleLeaf ? PALE_LEAF : tintOf(blockId);
      const grey = (kind === Kind.PaleLeaf ? 1 : 0.62) * vary;
      r = t[0] * grey; g = t[1] * grey; b = t[2] * grey;
    }
    this.col[i * 4] = Math.min(1, r);
    this.col[i * 4 + 1] = Math.min(1, g);
    this.col[i * 4 + 2] = Math.min(1, b);
    this.col[i * 4 + 3] = 0;
    this.pos[i * 3] = x;
    this.pos[i * 3 + 1] = y;
    this.pos[i * 3 + 2] = z;
    this.misc[i * 4] = def.scale * (Math.random() < 0.5 ? 0.1 : 0.15);
    this.misc[i * 4 + 1] = Math.random() * Math.PI * 2;
    this.misc[i * 4 + 2] = this.sky[i];
    this.misc[i * 4 + 3] = this.blk[i];
  }
}
